<!-- src/components/Layout/AlertsIndicator.vue -->
<template>
  <div ref="alertsMenuContainer" class="group relative">
    <button
      @click="toggleAlertsMenu"
      class="relative inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100"
      title="Alerts"
      aria-label="Alerts"
    >
      <Bell class="h-5 w-5 text-gray-700" />
      <span
        v-if="totalAlertsCount > 0"
        class="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white"
      >
        {{ totalAlertsCount > 99 ? '99+' : totalAlertsCount }}
      </span>
    </button>

    <div
      v-if="alertsMenuOpen"
      class="absolute right-0 z-dropdown mt-2 max-h-[calc(100dvh-5rem)] w-80 max-w-[calc(100vw-5rem)] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg sm:max-w-none"
    >
      <div class="flex items-center justify-between border-b p-3">
        <h3 class="font-semibold text-gray-800">Your Alerts</h3>
        <button class="text-xs text-blue-600 hover:underline" @click="openHub">Open Hub</button>
      </div>

      <div v-if="displayItems.length === 0" class="p-4 text-sm text-gray-500">No alerts 🎉</div>

      <ul v-else class="max-h-[420px] divide-y overflow-auto">
        <li
          v-for="it in displayItems"
          :key="it.id"
          class="px-3 py-2"
          :class="it.hasBadge ? 'bg-blue-50/60' : ''"
        >
          <div class="flex items-start gap-2">
            <div class="w-5 shrink-0 pt-0.5">
              <!-- Icon Logic -->
              <component :is="getAlertIcon(it)" :class="getAlertIconClass(it.kind)" />
            </div>

            <div class="flex-1">
              <div class="flex items-center justify-between">
                <div class="font-medium text-gray-800">
                  {{ getAlertTitle(it) }}
                </div>
                <div v-if="it.count > 1 || it.isConsolidated" class="text-xs text-gray-500">
                  {{ it.isConsolidated ? `+${it.count}` : `+${it.count}` }}
                </div>
              </div>

              <div v-if="getAlertSubtitle(it)" class="mt-0.5 text-xs text-gray-500">
                {{ getAlertSubtitle(it) }}
              </div>

              <div class="text-xs text-gray-600" :class="getAlertSubtitle(it) ? 'mt-0.5' : ''">
                <button class="text-blue-600 hover:underline" @click="handleAlertClick(it)">
                  {{ getAlertAction(it) }}
                </button>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div class="flex items-center justify-between border-t p-3 text-xs">
        <div class="flex items-center gap-3">
          <BusyButton
            class="text-gray-500 hover:text-gray-700 hover:underline"
            :busy="isClearingAlerts"
            busy-label="Clearing…"
            @click.stop="clearAllAlerts"
          >
            Clear Alerts
          </BusyButton>
          <span class="text-gray-400">Counts update in real time.</span>
        </div>
        <button
          class="text-blue-600 hover:text-blue-800 hover:underline"
          @click.stop="openSettings"
        >
          Alert Settings
        </button>
      </div>
    </div>
  </div>
  <AlertsHubModal
    :is-open="showHub"
    :alerts="visibleEphemerals"
    @close="showHub = false"
    @clear-all="clearAllAlerts"
    @dismiss="dismissAlert"
    @view="viewAlert"
    @notes="openNotesFromAlert"
  />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { db } from '@/services/firebaseconfig'
import {
  collection,
  writeBatch,
  getDocs,
  doc as fsDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { useUserStore } from '@/stores/userStore'
import preferenceCatalog from '../../../functions-shared/notifications/alertPreferenceCatalog'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { routeForAlert } from '@/utils/alertRouting'
import { useCommsNotificationStore } from '@/stores/commsNotificationStore'
import { useNoteNotificationStore } from '@/stores/noteNotificationStore'
import { useLeadPoolStore } from '@/stores/leadPoolStore'
import { useAlertPreferencesStore } from '@/stores/alertPreferencesStore'
import { useServiceCasesStore } from '@/stores/serviceCasesStore'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { playGeneratedTone } from '@/services/notificationSoundService'
import AlertsHubModal from '@/components/Notifications/AlertsHubModal.vue'
import BusyButton from '@/components/Common/BusyButton.vue'
import {
  Bell,
  Mail,
  MessageSquare,
  StickyNote,
  CheckCircle2,
  XCircle,
  Target,
  Wrench,
  Phone,
  CalendarCheck,
  CalendarX,
  CalendarClock,
  Voicemail,
  AtSign,
  RefreshCw,
  PauseCircle,
  PlayCircle,
  AlertTriangle,
  Package,
  PackageX,
  MessageCircle,
  Bug,
} from 'lucide-vue-next'

const router = useRouter()
const userStore = useUserStore()
const permStore = usePermissionsStore()
const commsStore = useCommsNotificationStore()
const noteStore = useNoteNotificationStore()
const leadPoolStore = useLeadPoolStore()
const serviceCasesStore = useServiceCasesStore()
const alertPrefsStore = useAlertPreferencesStore()
const flagsStore = useFrontendFlagsStore()

const alertsMenuOpen = ref(false)
const alertsMenuContainer = ref(null)
const showHub = ref(false)
const ephemerals = ref([])
// Re-evaluate a role change even if this inbox receives no new snapshot.
const visibleEphemerals = computed(() =>
  ephemerals.value.filter(alert =>
    preferenceCatalog.eventVisible(alert.kind, userStore.isSuperAdmin)
  )
)

let unsubEphemeral = null
let seenAlertIds = new Set()
let isInitialSnapshot = true

// Load preferences
alertPrefsStore.load?.()
// prefsStore.load removed — alertPrefsStore handles preferences

// Icon mapping
const iconMap = {
  emailAll: Mail,
  textAll: MessageSquare,
  noteAll: StickyNote,
  noteTagged: AtSign,
  unreadText: MessageSquare,
  unreadEmail: Mail,
  unreadChat: MessageCircle,
  customerCompleted: CheckCircle2,
  customerCanceled: XCircle,
  customerReactivated: RefreshCw,
  customerOnHold: PauseCircle,
  customerReleasedFromHold: PlayCircle,
  taskRejected: AlertTriangle,
  leadPool: Target,
  serviceCasesActive: Wrench,
  powerHubAlertAutoCreate: Wrench,
  missedCallBrowser: Phone,
  voicemail: Voicemail,
  unknownVoicemail: Voicemail,
  unknownText: MessageSquare,
  unknownEmail: Mail,
  calendarEventAccepted: CalendarCheck,
  calendarEventDeclined: CalendarX,
  calendarEventTentative: CalendarClock,
  workflowAlert: Bell, // For workflow/notification library alerts
  inventoryLowStock: Package,
  inventoryCritical: Package,
  kittingProblem: PackageX,
  bugReportSubmitted: Bug,
  bugReportStatusChanged: Bug,
}

const iconClassMap = {
  customerCompleted: 'w-4 h-4 text-emerald-600',
  customerCanceled: 'w-4 h-4 text-rose-600',
  taskRejected: 'w-4 h-4 text-rose-600',
  leadPool: 'w-4 h-4 text-brand-blue',
  serviceCasesActive: 'w-4 h-4 text-amber-600',
  powerHubAlertAutoCreate: 'w-4 h-4 text-amber-600',
  missedCallBrowser: 'w-4 h-4 text-red-600',
  voicemail: 'w-4 h-4 text-amber-600',
  unknownVoicemail: 'w-4 h-4 text-amber-600',
  unknownText: 'w-4 h-4 text-amber-600',
  unknownEmail: 'w-4 h-4 text-amber-600',
  calendarEventAccepted: 'w-4 h-4 text-green-600',
  calendarEventDeclined: 'w-4 h-4 text-red-600',
  calendarEventTentative: 'w-4 h-4 text-amber-600',
  communicationResolutionNeeded: 'w-4 h-4 text-orange-600',
  workflowAlert: 'w-4 h-4 text-brand-blue', // Workflow/notification library alerts
  inventoryLowStock: 'w-4 h-4 text-amber-600',
  inventoryCritical: 'w-4 h-4 text-red-600',
  kittingProblem: 'w-4 h-4 text-red-600',
  unreadChat: 'w-4 h-4 text-emerald-600',
  bugReportSubmitted: 'w-4 h-4 text-brand-blue',
  bugReportStatusChanged: 'w-4 h-4 text-brand-blue',
}

const getAlertSubtitle = item => {
  if (item.kind === 'powerHubAlertAutoCreate') {
    return item.subtitle || item.title || 'Service Case Auto-Created'
  }
  if (item.kind === 'communicationResolutionNeeded') {
    // Construct locally for correct user timezone
    const extra = item.extra || {}
    if (extra.commTimestamp) {
      let ts
      if (extra.commTimestamp.toDate) ts = extra.commTimestamp.toDate()
      else if (extra.commTimestamp.seconds) ts = new Date(extra.commTimestamp.seconds * 1000)
      else ts = new Date(extra.commTimestamp)
      const timeStr = ts.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
      const type =
        (extra.commType || 'Communication').charAt(0).toUpperCase() +
        (extra.commType || '').slice(1)
      const name = item.customerName || 'Customer'
      return `${type} from ${name} at ${timeStr} needs response`
    }
    if (item.subtitle) return item.subtitle
    const h = item.hoursElapsed || 0
    return h > 0 ? `No response for ${h === 1 ? '1 hour' : `${h} hours`}` : null
  }
  if (item.kind === 'voicemail' || item.kind === 'unknownVoicemail') {
    const parts = []
    if (item.callerIdName) parts.push(item.callerIdName)
    if (item.fromNumber) parts.push(item.fromNumber)
    return parts.length > 0 ? parts.join(' – ') : null
  }
  if (item.kind === 'missedCallBrowser' || item.kind === 'missedCall') {
    return item.fromNumber || null
  }
  // Unknown senders: the backend's subtitle is the text's first words or the email's subject.
  if (item.kind === 'unknownText' || item.kind === 'unknownEmail') {
    return item.subtitle || item.preview || item.subject || null
  }
  return item.subtitle || null
}

const getAlertIcon = item => {
  // Handle dynamic icons for Resolution Needed
  if (item.kind === 'communicationResolutionNeeded') {
    const type = item.commType || item.extra?.commType || ''
    if (type === 'email') return Mail
    if (type === 'phone' || type === 'voicemail') return Phone
    return MessageSquare // Default to text
  }

  // Handle workflow alerts (workflow_*)
  if (item.kind?.startsWith('workflow_')) {
    return Bell
  }

  // Fallback to static map
  return iconMap[item.kind] || Bell
}
const getAlertIconClass = kind => {
  if (kind?.startsWith('workflow_')) {
    return 'w-4 h-4 text-brand-blue'
  }
  return iconClassMap[kind] || 'w-4 h-4 text-gray-700'
}

// Title generation
const getAlertTitle = item => {
  const kind = item.kind
  const name = item.customerName
  const count = item.count
  const isConsolidated = item.isConsolidated

  // ✅ NEW: Handle PowerHub alerts FIRST (before workflow check)
  if (kind === 'powerHubAlertAutoCreate') {
    // Use customer name if available, otherwise use backend title
    if (name) return name
    if (item.title) return item.title
    return 'PowerHub Alert'
  }

  // Handle workflow alerts (workflow_*) - show customer name or title
  if (kind?.startsWith('workflow_')) {
    if (isConsolidated) return 'Workflow Alerts'
    // Use custom title if provided, otherwise show customer name
    if (item.title && name) {
      return name // Show customer name as primary
    }
    if (name) return name
    if (item.title) return item.title
    return 'Workflow Alert'
  }

  // Consolidated titles
  if (isConsolidated) {
    const titles = {
      unreadText: 'New Texts',
      unreadEmail: 'New Emails',
      noteTagged: 'Tagged in Notes',
      noteAll: 'New Notes',
      voicemail: 'Voicemails',
      unknownVoicemail: 'Unknown Voicemails',
      unknownText: 'Texts from Unknown Numbers',
      unknownEmail: 'Emails from Unknown Senders',
      missedCall: 'Missed Calls',
      missedCallBrowser: 'Missed Calls',
      customerCompleted: 'Customers Completed',
      customerCanceled: 'Customers Canceled',
      customerReactivated: 'Customers Reactivated',
      customerOnHold: 'Customers On Hold',
      customerReleasedFromHold: 'Customers Released',
      taskRejected: 'Tasks Rejected',
      calendarEventAccepted: 'Events Accepted',
      calendarEventDeclined: 'Events Declined',
      calendarEventTentative: 'Events Tentative',
      communicationResolutionNeeded: 'Response Needed',
      powerHubAlertAutoCreate: 'PowerHub Alerts',
      workflowAlert: 'Workflow Alerts',
      inventoryLowStock: 'Low Stock Alerts',
      inventoryCritical: 'Critical Stock Alerts',
      kittingProblem: 'Kitting Problems',
      bugReportSubmitted: 'Reports & Requests',
      bugReportStatusChanged: 'Report Updates',
    }
    return titles[kind] || 'Alerts'
  }

  // Single item titles
  const titles = {
    emailAll: 'Emails',
    textAll: 'Texts',
    noteAll: 'Notes',
    noteTagged: 'Notes (Tagged)',
    unreadText: count === 1 && name ? `New Text – ${name}` : 'New Texts',
    unreadEmail: count === 1 && name ? `New Email – ${name}` : 'New Emails',
    unreadChat: count === 1 && name ? `New Chat – ${name}` : 'New Chats',
    customerCompleted: count === 1 && name ? `${name} Completed` : 'Customers Completed',
    customerCanceled: count === 1 && name ? `${name} Canceled` : 'Customers Canceled',
    customerReactivated: count === 1 && name ? `${name} Reactivated` : 'Customers Reactivated',
    customerOnHold: count === 1 && name ? `${name} On Hold` : 'Customers On Hold',
    customerReleasedFromHold: count === 1 && name ? `${name} Released` : 'Customers Released',
    taskRejected: count === 1 && name ? `${name} Task Rejected` : 'Tasks Rejected',
    leadPool: 'Lead Pool',
    serviceCasesActive: 'Open Service Cases',
    missedCallBrowser: name
      ? `Missed Call – ${name}`
      : item.fromNumber
        ? `Missed Call – ${item.fromNumber}`
        : 'Missed Call',
    voicemail: count === 1 && name ? `Voicemail – ${name}` : 'Voicemails',
    unknownVoicemail: item.fromNumber ? `Voicemail – ${item.fromNumber}` : 'Unknown Voicemail',
    unknownText: item.fromNumber ? `Text – ${item.fromNumber}` : 'Text from Unknown Number',
    unknownEmail: item.fromEmail ? `Email – ${item.fromEmail}` : 'Email from Unknown Sender',
    calendarEventAccepted: 'Event Accepted',
    calendarEventDeclined: 'Event Declined',
    calendarEventTentative: 'Event Tentative',
    communicationResolutionNeeded:
      item.title || (name ? `Response Needed – ${name}` : 'Response Needed'),
    powerHubAlertAutoCreate: name || 'PowerHub Alert',
    workflowAlert: item.title || 'Workflow Alert',
    inventoryLowStock:
      item.title ||
      (item.extra?.catalogName ? `Low Stock – ${item.extra.catalogName}` : 'Low Stock Alert'),
    inventoryCritical:
      item.title ||
      (item.extra?.catalogName
        ? `Critical Stock – ${item.extra.catalogName}`
        : 'Critical Stock Alert'),
    kittingProblem: item.title || (name ? `${name} – Kitting Problem` : 'Kitting Problem'),
    bugReportSubmitted:
      item.title ||
      (() => {
        const t = (item.reportType || item.extra?.reportType) === 'feature' ? 'Feature' : 'Bug'
        const n = item.bugTitle || item.extra?.bugTitle
        return n ? `${t} filed: ${n}` : `${t} Report Filed`
      })(),
    bugReportStatusChanged:
      item.title ||
      (() => {
        const t = (item.reportType || item.extra?.reportType) === 'feature' ? 'Feature' : 'Bug'
        const n = item.bugTitle || item.extra?.bugTitle
        return n ? `${t} update: ${n}` : `${t} Report Updated`
      })(),
  }
  return titles[kind] || item.title || 'Alert'
}

// Action text
const getAlertAction = item => {
  const kind = item.kind

  // ✅ NEW: Handle PowerHub alerts
  if (kind === 'powerHubAlertAutoCreate') {
    return item.customerId ? 'View customer' : 'Open hub'
  }

  // Handle workflow alerts
  if (kind?.startsWith('workflow_')) {
    return item.customerId ? 'View customer' : 'Open hub'
  }

  const actions = {
    systemMonitoring: 'View monitor',
    emailAll: 'Open Emails',
    textAll: 'Open Texts',
    noteAll: 'Open Notes',
    noteTagged: 'Open Notes',
    unreadText: 'View customer',
    unreadEmail: 'View customer',
    customerCompleted: 'View customer',
    customerCanceled: 'View customer',
    customerReactivated: 'View customer',
    customerOnHold: 'View customer',
    customerReleasedFromHold: 'View customer',
    taskRejected: 'Open hub',
    leadPool: 'View lead pool',
    serviceCasesActive: 'View service cases',
    missedCallBrowser: 'Open hub',
    voicemail: 'Open hub',
    unknownVoicemail: 'Open Communications',
    unknownText: 'Open Unknown',
    unknownEmail: 'Open Unknown',
    calendarEventAccepted: 'View details',
    calendarEventDeclined: 'View details',
    calendarEventTentative: 'View details',
    communicationResolutionNeeded: 'View communication',
    // ✅ NEW: Add PowerHub entry (backup)
    powerHubAlertAutoCreate: 'View customer',
    inventoryLowStock: 'View inventory',
    inventoryCritical: 'View inventory',
    kittingProblem: 'View kitting',
    bugReportSubmitted: 'View bug report',
    bugReportStatusChanged: 'View bug report',
  }
  return actions[kind] || 'Open hub'
}

// Bell-menu click — routing DELEGATES to the shared map (alertRouting.js,
// K3P0DV7j reconciliation), keeping only this surface's deliberate specials:
// the dropdown-only aggregate rows, and open-the-hub when a contextless
// alert has nowhere better to go.
const HUB_WHEN_NO_CUSTOMER = new Set(['powerHubAlertAutoCreate'])
const handleAlertClick = item => {
  const kind = item.kind

  if (kind === 'emailAll' || kind === 'textAll') {
    router.push({ name: 'Communications', query: { tab: 'comms' } })
  } else if (
    !item.customerId &&
    (HUB_WHEN_NO_CUSTOMER.has(kind) || kind?.startsWith('workflow_'))
  ) {
    openHub()
    return
  } else {
    router.push(routeForAlert(item))
  }
  alertsMenuOpen.value = false
}

const openSettings = () => {
  router.push({ name: 'Settings' })
  alertsMenuOpen.value = false
}

const openNotesFromAlert = alert => {
  if (!alert?.customerId) return
  const query = { openModal: 'notes' }
  if (alert.kind === 'taskRejected' && alert.taskId) {
    query.taskId = alert.taskId
  } else if (alert.stage) {
    query.stage = alert.stage
  } else {
    query.stage = 'General'
  }
  router.push({
    name: 'CustomerCommandCenter',
    params: { id: alert.customerId },
    query,
  })
  showHub.value = false
}

const openHub = () => {
  showHub.value = true
  alertsMenuOpen.value = false
}

const isClearingAlerts = ref(false)
const clearAllAlerts = async () => {
  // Guard-first — a double-click enumerated + batch-updated the whole alert
  // subcollection twice (button-guard burn-down, 2026-08-16).
  if (isClearingAlerts.value) return
  const uid = userStore.user?.uid
  if (!uid) return
  isClearingAlerts.value = true

  try {
    const ephRef = collection(db, `users/${uid}/ephemeralAlerts`)
    const snap = await getDocs(ephRef)
    if (snap.empty) return

    const batch = writeBatch(db)
    snap.forEach(docSnap => {
      const x = docSnap.data() || {}
      if (x.dismissed) return
      batch.update(docSnap.ref, {
        dismissed: true,
        dismissedAt: serverTimestamp(),
      })
    })
    await batch.commit()
  } catch (e) {
    console.error('[AlertsIndicator] Failed to clear alerts:', e)
  } finally {
    isClearingAlerts.value = false
  }
}

const dismissAlert = async alert => {
  const uid = userStore.user?.uid
  if (!uid || !alert?.id) return
  try {
    const ref = fsDoc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
    await updateDoc(ref, {
      dismissed: true,
      dismissedAt: serverTimestamp(),
    })
  } catch (e) {
    console.error('[AlertsIndicator] Failed to dismiss alert:', e)
  }
}

// Routing lives in the SHARED map (src/utils/alertRouting.js, K3P0DV7j) —
// this surface and NotificationOverlay had drifted copies; new alert kinds
// get their route there, once.
const viewAlert = alert => {
  if (!alert) return
  router.push(routeForAlert(alert))
  // ✅ Do NOT auto-dismiss alerts just because they were viewed.
  showHub.value = false
}

/**
 * Check if user has access to this alert based on role/org
 */
const userHasAccessToAlert = alertData => {
  const user = userStore.user
  if (!user) return false
  if (!preferenceCatalog.eventVisible(alertData.kind, userStore.isSuperAdmin)) return false

  const visibility = alertData.visibility || 'self'
  const roles = userStore.roles || []
  const _isAdmin = userStore.isAdmin
  const isSuperAdmin = userStore.isSuperAdmin
  const userEmail = user.email?.toLowerCase()
  const userOrgId = user.organizationId
  const isOrgOwner = user.orgRole === 'owner'

  // Full-visibility roles and super admins see all alerts (driven by access control matrix)
  if (isSuperAdmin || permStore.hasFullVisibility('customers', roles, isSuperAdmin)) return true

  // Check visibility level
  switch (visibility) {
    case 'all':
      // Everyone can see
      return true

    case 'org':
      // Must be in same org
      if (!userOrgId || !alertData.organizationId) return true // No org restriction
      return userOrgId === alertData.organizationId

    case 'owners': {
      // Must be owner of the customer OR org owner
      if (isOrgOwner && userOrgId === alertData.organizationId) return true

      const ownerEmails = (alertData.ownerEmails || []).map(e => e?.toLowerCase())
      const ownerUids = alertData.ownerUids || []

      if (ownerEmails.includes(userEmail)) return true
      if (ownerUids.includes(user.uid)) return true

      return false
    }

    case 'self':
    default:
      // Alert was created for this user specifically, always show
      return true
  }
}

const startListeners = () => {
  const uid = userStore.user?.uid
  if (!uid) return

  seenAlertIds = new Set()
  isInitialSnapshot = true

  // Force re-subscribe to ensure listeners are active (module-aware)
  if (flagsStore.isModuleEnabled('notifications')) {
    commsStore.unsubscribe?.()
    noteStore.unsubscribe?.()
    commsStore.subscribe?.()
    noteStore.subscribe?.()
  }
  if (flagsStore.isModuleEnabled('leadManagement')) {
    leadPoolStore.subscribe?.()
  }
  if (flagsStore.isModuleEnabled('serviceCases')) {
    const scOrgId = permStore.hasFullVisibility(
      'serviceCases',
      userStore.roles,
      userStore.isSuperAdmin
    )
      ? null
      : userStore.user?.organizationId || null
    serviceCasesStore.subscribeActive({ organizationId: scOrgId })
  }

  console.log('[AlertsIndicator] Starting ephemeral subscription for uid:', uid)

  // Query ephemeral alerts - filtering by visibility on client side
  const ephRef = collection(db, `users/${uid}/ephemeralAlerts`)
  unsubEphemeral = onSnapshot(
    ephRef,
    snap => {
      const rows = []
      let shouldPlaySound = false

      snap.forEach(d => {
        const x = d.data() || {}

        // Skip dismissed alerts
        if (x.dismissed) return

        // Role/org access check
        if (!userHasAccessToAlert(x)) {
          return
        }

        // Check if this is a new alert with sound enabled
        if (!isInitialSnapshot && !seenAlertIds.has(d.id)) {
          if (Array.isArray(x.deliveredVia) && x.deliveredVia.includes('sound')) {
            shouldPlaySound = true
          }
        }
        seenAlertIds.add(d.id)

        rows.push({
          id: d.id,
          kind: x.kind || 'alert',
          customerId: x.customerId || null,
          customerName: x.customerName || null,
          // ✅ Pass Backend Title/Subtitle/Data to fix Hub display
          title: x.title || null,
          subtitle: x.subtitle || null,
          hoursElapsed: x.hoursElapsed || 0,
          commId: x.commId || null,
          commType: x.commType || null,
          // Voice/call fields (spread at root by backend)
          fromNumber: x.fromNumber || null,
          callerIdName: x.callerIdName || null,

          holdIsInternal: !!x.holdIsInternal,
          actorUid: x.actorUid || null,
          actorEmail: x.actorEmail || null,
          taskId: x.taskId || null,
          taskDisplayName: x.taskDisplayName || null,
          at: x.at?.toDate?.() || null,
          // Calendar event fields
          attendeeName: x.attendeeName || null,
          eventSummary: x.eventSummary || null,
          eventStart: x.eventStart || null,
          googleEventId: x.googleEventId || null,
          serviceCaseId: x.serviceCaseId || null,
          miscEventId: x.miscEventId || null,
          // Bug report fields (flattened at root by deliverAlert)
          bugReportId: x.bugReportId || null,
          bugTitle: x.bugTitle || null,
          reportType: x.reportType || null,
          // Delivery channels
          deliveredVia: Array.isArray(x.deliveredVia) ? x.deliveredVia : [],
          // Access control
          organizationId: x.organizationId || null,
          ownerEmails: x.ownerEmails || [],
          visibility: x.visibility || 'self',
          // Keep raw extra for deep linking logic
          extra: x.extra || {},
          monitorJobId: x.monitorJobId || null,
          siteId: x.siteId || null,
          submissionPath: x.submissionPath || null,
          mretsRunId: x.mretsRunId || null,
          monitorIncidentId: x.monitorIncidentId || null,
          resolved: x.resolved === true,
        })
      })

      // Sort by at desc (newest first)
      rows.sort((a, b) => (b.at?.getTime?.() || 0) - (a.at?.getTime?.() || 0))

      // Filter missed call alerts if unavailable
      const available = !!userStore.user?.availableForCalls
      const showWhenUnavailable = true // Default: always show missed call alerts (legacy pref removed)

      const filtered = rows.filter(r => {
        if (r.kind !== 'missedCallBrowser') return true
        if (available) return true
        return !!showWhenUnavailable
      })

      ephemerals.value = filtered

      if (shouldPlaySound) {
        playGeneratedTone()
      }

      isInitialSnapshot = false
      console.log('[AlertsIndicator] Final ephemerals count:', filtered.length)
    },
    error => {
      console.error('[AlertsIndicator] Ephemeral subscription error:', error)
    }
  )
}

const stopListeners = () => {
  if (unsubEphemeral) {
    unsubEphemeral()
    unsubEphemeral = null
  }
  serviceCasesStore.unsubscribeAll()
}

// Visibility handling for tab focus
onMounted(() => {
  const handleVisibility = () => {
    if (!document.hidden && userStore.user?.uid && flagsStore.isModuleEnabled('notifications')) {
      if (!commsStore.isSubscribed) commsStore.subscribe?.()
      if (!noteStore.isSubscribed) noteStore.subscribe?.()
    }
  }

  document.addEventListener('visibilitychange', handleVisibility)
  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibility)
  })
})

/**
 * Apply consolidation to items
 */
const applyConsolidation = items => {
  const result = []
  const groupedByKind = {}

  // Group items by kind
  for (const item of items) {
    const kind = item.kind
    if (!groupedByKind[kind]) {
      groupedByKind[kind] = []
    }
    groupedByKind[kind].push(item)
  }

  // Apply consolidation per kind
  for (const [kind, kindItems] of Object.entries(groupedByKind)) {
    const consolidation = alertPrefsStore.getConsolidation(kind)

    if (
      consolidation.enabled &&
      consolidation.after > 0 &&
      kindItems.length > consolidation.after
    ) {
      // Show first N items individually
      const individualItems = kindItems.slice(0, consolidation.after)
      for (const item of individualItems) {
        result.push({ ...item, count: 1, isConsolidated: false })
      }

      // Consolidate the rest
      const consolidatedCount = kindItems.length - consolidation.after
      const representativeItem = kindItems[consolidation.after] // Use first consolidated item as representative
      result.push({
        id: `consolidated-${kind}`,
        kind,
        count: consolidatedCount,
        isConsolidated: true,
        customerId: representativeItem.customerId,
        customerName: null, // Don't show name for consolidated
        hasBadge: kindItems
          .slice(consolidation.after)
          .some(i => Array.isArray(i.deliveredVia) && i.deliveredVia.includes('badge')),
      })
    } else {
      // No consolidation, show all
      for (const item of kindItems) {
        result.push({ ...item, count: 1, isConsolidated: false })
      }
    }
  }

  return result
}

/**
 * Display items - combines comms, notes, ephemerals with consolidation
 */
const displayItems = computed(() => {
  const rows = []

  // Aggregated comms counts (only when notifications module enabled)
  if (flagsStore.isModuleEnabled('notifications')) {
    let totalEmail = 0,
      totalText = 0
    const byCid = commsStore.unreadCommsByCustomerId || {}
    Object.values(byCid).forEach(v => {
      totalEmail += v.unreadEmailCount || 0
      totalText += v.unreadTextCount || 0
    })
    if (totalEmail > 0)
      rows.push({ id: 'email-all', kind: 'emailAll', count: totalEmail, hasBadge: true })
    if (totalText > 0)
      rows.push({ id: 'text-all', kind: 'textAll', count: totalText, hasBadge: true })

    // Notes
    if (noteStore.totalUnreadCount > 0) {
      rows.push({
        id: 'note-all',
        kind: 'noteAll',
        count: noteStore.totalUnreadCount,
        hasBadge: false,
      })
    }
  }

  // Filter ephemerals to only those with dropdown delivery
  // Exclude unreadText/unreadEmail since they duplicate textAll/emailAll
  const dropdownEphemerals = (visibleEphemerals.value || []).filter(e => {
    if (e.kind === 'unreadText' || e.kind === 'unreadEmail') return false

    // Check if dropdown is enabled for this alert
    if (Array.isArray(e.deliveredVia) && e.deliveredVia.length > 0) {
      return e.deliveredVia.includes('dropdown')
    }
    // Fallback for legacy alerts
    return true
  })

  // Apply consolidation to ephemerals
  const consolidatedEphemerals = applyConsolidation(dropdownEphemerals)

  // Add consolidated ephemerals
  for (const item of consolidatedEphemerals) {
    const hasBadge =
      item.hasBadge ?? (Array.isArray(item.deliveredVia) && item.deliveredVia.includes('badge'))
    rows.push({
      ...item,
      hasBadge,
    })
  }

  // Service cases (only when serviceCases module enabled)
  if (flagsStore.isModuleEnabled('serviceCases') && (serviceCasesStore.activeCount || 0) > 0) {
    rows.push({
      id: 'service-cases',
      kind: 'serviceCasesActive',
      count: serviceCasesStore.activeCount,
      hasBadge: false,
    })
  }

  // Lead pool (only when leadManagement module enabled)
  if (flagsStore.isModuleEnabled('leadManagement') && leadPoolStore.leadCount > 0) {
    rows.push({
      id: 'lead-pool',
      kind: 'leadPool',
      count: leadPoolStore.leadCount,
      hasBadge: false,
    })
  }

  return rows
})

/**
 * Total badge count
 */
const totalAlertsCount = computed(() => {
  const commsCount = flagsStore.isModuleEnabled('notifications')
    ? Number(commsStore.totalUnreadCount || 0)
    : 0

  // Count ephemerals with badge delivery, excluding unreadText/unreadEmail
  const ephemeralBadgeCount = (visibleEphemerals.value || []).filter(e => {
    if (e.kind === 'unreadText' || e.kind === 'unreadEmail') return false

    if (Array.isArray(e.deliveredVia) && e.deliveredVia.length > 0) {
      return e.deliveredVia.includes('badge')
    }
    // Legacy fallback
    return e.kind === 'voicemail' || e.kind === 'unknownVoicemail' || e.kind === 'noteTagged'
  }).length

  return commsCount + ephemeralBadgeCount
})

function toggleAlertsMenu() {
  alertsMenuOpen.value = !alertsMenuOpen.value
}

function handleClickOutside(e) {
  if (alertsMenuContainer.value && !alertsMenuContainer.value.contains(e.target)) {
    alertsMenuOpen.value = false
  }
}

// Start listeners when user is available
watch(
  () => userStore.user?.uid,
  (uid, oldUid) => {
    console.log('[AlertsIndicator] User changed:', { uid, oldUid })
    stopListeners()
    if (uid) {
      startListeners()
    }
  },
  { immediate: true }
)

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  stopListeners()
})
</script>
